import React, { useEffect, useMemo, useState } from 'react';
import { Button, Skeleton } from 'antd';
import classNames from 'classnames';
import { GetStaticProps } from 'next';
import { useTranslations } from 'next-intl';
import { uid } from 'uid';

import { useAppDispatch } from 'store';

import { blokTestConst, QuizCard, useStartedTest } from 'modules/blok-test';
import { setConfirmationModal, useConfirmBackNavigation, useScrollSpy } from 'modules/global';

import { ConfirmationModal, NextHead, TestEye, TestProgressModal, TestTime } from 'components';

const VirtualTest = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const { startedTest, isLoading } = useStartedTest('virtual');
  const [openTestProgress, setOpenTestProgress] = useState(false);
  const [selectedQuizIndex, setSelectedQuizIndex] = useState(0);

  useConfirmBackNavigation(() => {
    dispatch(setConfirmationModal(true));
  });

  const initiallyAnswered = useMemo(
    () => startedTest?.test?.flatMap(test => test?.questions?.filter(q => q.checked)).map(q => q.id) || [],
    [startedTest]
  );

  const [answeredIds, setAnsweredIds] = useState<number[]>(initiallyAnswered);

  const onAnswered = (questionId: number) => {
    setAnsweredIds(prev => (prev.includes(questionId) ? prev : [...prev, questionId]));
  };

  const onQuizNameClick = (quizId: number, index: number) => () => {
    setSelectedQuizIndex(index);
    const element = document.getElementById(`quiz-${quizId}-${index}`);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const totalQuestions = startedTest?.test?.reduce((acc, curr) => acc + curr.questions.length, 0) || 0;
  const progress = Math.round((answeredIds.length / totalQuestions) * 100);
  const quizIds = startedTest?.test?.map((test, index) => `quiz-${test?.id}-${index}`) || [];

  useScrollSpy({
    itemIds: quizIds,
    onActiveChange: setSelectedQuizIndex
  });

  useEffect(() => {
    setAnsweredIds(initiallyAnswered);
  }, [startedTest, initiallyAnswered]);

  return (
    <>
      <NextHead title={t('meta.virtual_test.title')} description={t('meta.virtual_test.description')} />

      <div className="m-0 mx-auto min-h-screen max-w-[1150px] p-4 lg:my-4">
        <div className="flex items-center justify-between">
          <div className="space-x-3 rounded-xl bg-indigo-600 px-4 py-1 text-white lg:hidden">
            <span>{t('savollar')}</span>
            <span>
              {answeredIds.length}/{totalQuestions}
            </span>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-4 lg:gap-3">
          {/* CHAP TOMON */}
          <div className="hidden space-y-3 pt-4 self-start md:top-0 lg:sticky lg:block">
            <div className="shadow-xs  rounded-xl bg-white p-3 ">
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <div className="inline rounded-md bg-gray-100 px-2 py-1 text-sm">Virtual</div>
              <div className="mt-4 space-y-2 ">
                {startedTest?.test?.map((quiz, index) => (
                  <div
                    onClick={onQuizNameClick(quiz.id, index)}
                    className={classNames(
                      'flex cursor-pointer items-center justify-between rounded-xl border  px-3 py-2',
                      {
                        'border-green-500': selectedQuizIndex === index,
                        'border-gray-200': selectedQuizIndex !== index
                      }
                    )}
                    key={uid()}
                  >
                    <div className="font-semibold">
                      {index + 1}. {quiz.name}
                    </div>{' '}
                    <div>
                      {quiz.questions.length} {t('ta')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div />
            <div className="shadow-xs hidden rounded-xl bg-white p-3 lg:block">
              <h1 className="text-md font-medium ">{t('savollar')}</h1>
              <div className="mb-6 mt-3 flex items-center gap-2">
                <div className="flex-1 rounded-full border-2 border-[#C4C4C4] p-0.5">
                  <div className="h-2 w-full rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-green-500"
                      style={{
                        width: `${progress}%`
                      }}
                    />
                  </div>
                </div>
                <div className="text-sm font-medium text-green-500">
                  {answeredIds.length}/{totalQuestions}
                </div>
              </div>
            </div>
          </div>

          {/* O"NG TOMON */}
          <div className="sm:col-span-3">
            {isLoading ? (
              <div className="space-y-3">
                <div className="flex w-full items-center justify-between rounded-xl bg-white px-4 py-3">
                  <div className="space-x-2">
                    <Skeleton.Button active />
                    <Skeleton.Button active />
                  </div>
                  <div>
                    <Skeleton.Input active />
                  </div>
                </div>

                <div className="shadow-xs rounded-xl bg-white px-4 py-3">
                  <div className="mb-5 flex items-center justify-between">
                    <Skeleton.Button active className="!h-2" />
                    <Skeleton.Input active className="!h-2" />
                  </div>
                  <Skeleton active />
                </div>
                <div className="shadow-xs mb-4 rounded-xl bg-white px-4 py-3">
                  <div className="space-y-3">
                    <Skeleton.Input active className="!h-12 !w-full !rounded-xl" />
                    <Skeleton.Input active className="!h-12 !w-full !rounded-xl" />
                    <Skeleton.Input active className="!h-12 !w-full !rounded-xl" />
                    <Skeleton.Input active className="!h-12 !w-full !rounded-xl" />
                    <div className="mt-5 flex items-center justify-between">
                      <Skeleton.Button active className="!w-30 !h-10 !rounded-lg" />
                      <Skeleton.Button active className="!w-35 !h-10 !rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="lg:space-y-3">
                <div className="z-11 fixed bottom-0 left-0 right-0 bg-primary lg:sticky lg:top-0 lg:rounded-b-xl lg:pt-4">
                  <div className="flex items-center justify-between rounded-t-xl  bg-white px-4 py-3 shadow-sm lg:rounded-xl">
                    <div className="flex items-center gap-3">
                      <TestTime time={startedTest?.started_time} />
                      <TestEye onOpen={() => setOpenTestProgress(true)} />
                    </div>
                    <div>
                      <Button
                        onClick={() => dispatch(setConfirmationModal(true))}
                        className="!duration-400 !rounded-lg !border-none !bg-red-50 !px-8 !py-5 !font-medium !text-red-500 !transition !ease-in-out hover:!bg-red-500 hover:!text-white"
                      >
                        {t('yakunlash')}
                      </Button>
                    </div>
                  </div>
                </div>

                {startedTest?.test?.map((test, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`quiz-${test?.id}-${index}`}>
                    <div
                      id={`quiz-${test?.id}-${index}`}
                      className="scroll-mt-22 sticky top-0 z-10 mb-3 flex items-center justify-between rounded-xl bg-white p-4 text-green-500 lg:static"
                    >
                      {test.name}
                    </div>
                    {test?.questions?.map((question, index) => (
                      <div key={question.id}>
                        <QuizCard
                          test={question}
                          key={question?.id}
                          testName={test?.name}
                          onAnswer={onAnswered}
                          currentQuestion={index + 1}
                          questionsLength={test?.questions?.length}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <TestProgressModal
          answeredIds={answeredIds}
          data={startedTest?.test}
          title={t('virtual_test_natijalari')}
          open={openTestProgress}
          onClose={() => setOpenTestProgress(false)}
        />
        <ConfirmationModal testType={blokTestConst.testTypes.VIRTUAL_TEST} ticketId={startedTest?.ticket_id} />
      </div>
    </>
  );
};

export default VirtualTest;

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    messages: (await import(`../../messages/${locale}.json`)).default
  }
});
